import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HelpCircle,
  Users,
  ArrowRight,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuthStore } from "@/stores/auth";

// New Dashboard v2 components
import { GettingStartedChecklist } from "@/components/dashboard/GettingStartedChecklist";
import { EnforcementJourneyWidget } from "@/components/dashboard/EnforcementJourneyWidget";
import { NeedsAttentionCard } from "@/components/dashboard/NeedsAttentionCard";
import { QuickStatsCard } from "@/components/dashboard/QuickStatsCard";
import { AIRecommendationsWidget } from "@/components/dashboard/AIRecommendationsWidget";

interface DashboardStats {
  totalDomains: number;
  domainsAtEnforcement: number;
  enforcementPercentage: number;
  totalEmailsProcessed: number;
  passRate: number;
  failRate: number;
  activeAlerts: number;
  pendingTasks: number;
  totalMessages: number;
  dmarcPassCount: number;
  dmarcFailCount: number;
  spfPassCount: number;
  spfFailCount: number;
  dkimPassCount: number;
  dkimFailCount: number;
}

interface SourceStats {
  total: number;
  approved: number;
  pending: number;
  unauthorized: number;
  labeled?: number;
}

export function Dashboard() {
  const navigate = useNavigate();
  const { organization, activeClientOrg } = useAuthStore();

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [sourceStats, setSourceStats] = useState<SourceStats | null>(null);
  const [trends, setTrends] = useState<{ passRateTrend: number; messagesTrend: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [invitePromptDismissed, setInvitePromptDismissed] = useState(() => {
    return localStorage.getItem("verkh_invite_prompt_dismissed") === "true";
  });

  // Show invite prompt for paid users who haven't dismissed it
  // This encourages team collaboration for paid tiers
  const isPaidTier = organization?.billing_tier && organization.billing_tier !== "free";
  const showInvitePrompt = isPaidTier && !invitePromptDismissed;

  function dismissInvitePrompt() {
    localStorage.setItem("verkh_invite_prompt_dismissed", "true");
    setInvitePromptDismissed(true);
  }

  useEffect(() => {
    let cancelled = false;

    async function fetchDashboardData() {
      try {
        // Use the compound dashboard endpoint - ONE API call instead of 5-6
        const summary = await api.getDashboardSummary();

        if (cancelled) return;

        // Set all data from the single response
        // Use sender-level counts (provider-first) if available, fall back to IP-level
        setSourceStats(summary.senders || summary.sources);
        if (summary.trends) setTrends(summary.trends);

        setStats({
          totalDomains: summary.enforcement.totalDomains,
          domainsAtEnforcement: summary.enforcement.domainsAtEnforcement,
          enforcementPercentage: summary.enforcement.enforcementPercentage,
          totalEmailsProcessed: summary.emailStats.totalMessages,
          passRate: summary.emailStats.passRate,
          failRate: 100 - summary.emailStats.passRate,
          activeAlerts: summary.alerts.filter((a) => a.status === "open").length,
          pendingTasks: summary.remediationTasks.pending + summary.remediationTasks.inProgress,
          totalMessages: summary.emailStats.totalMessages,
          dmarcPassCount: summary.emailStats.dmarcPassCount,
          dmarcFailCount: summary.emailStats.dmarcFailCount,
          spfPassCount: summary.emailStats.spfPassCount,
          spfFailCount: summary.emailStats.spfFailCount,
          dkimPassCount: summary.emailStats.dkimPassCount,
          dkimFailCount: summary.emailStats.dkimFailCount,
        });
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchDashboardData();

    // Listen for MSP client context changes
    const handleClientChange = () => {
      fetchDashboardData();
    };
    window.addEventListener("msp-client-changed", handleClientChange);

    return () => {
      cancelled = true;
      window.removeEventListener("msp-client-changed", handleClientChange);
    };
  }, [activeClientOrg?.id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Show new dashboard layout for everyone
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-800">Dashboard</h1>
          <p className="text-sm text-slate-500">
            Your email authentication command center
          </p>
        </div>
        <Link
          to="/help#feature-dashboard"
          className="flex items-center gap-1.5 text-xs md:text-sm text-slate-400 hover:text-amber-600 transition-colors"
        >
          <HelpCircle className="w-4 h-4" />
          <span className="hidden sm:inline">Need help?</span>
        </Link>
      </div>

      {/* Getting Started Checklist (for new users) */}
      <GettingStartedChecklist isPaidTier={!!isPaidTier} />

      {/* Invite Team Prompt for Paid Users (only show if checklist dismissed or no longer showing) */}
      {showInvitePrompt && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 mb-1">Invite your team</h3>
              <p className="text-sm text-slate-600">
                Collaborate with your team on email authentication. Your plan includes multiple team members.
              </p>
              <button
                onClick={() => navigate("/settings?tab=team")}
                className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-amber-700 hover:text-amber-800"
              >
                Invite team members
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <button
            onClick={dismissInvitePrompt}
            className="p-1 text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Primary View: Enforcement Journey Widget */}
      <EnforcementJourneyWidget />

      {/* Two-column layout for AI + Quick Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* AI Recommendations - Proactive insights without user action */}
        <AIRecommendationsWidget />

        {/* Quick Stats Card */}
        <QuickStatsCard
          dmarcPassRate={stats?.passRate || 0}
          passRateTrend={trends?.passRateTrend}
          messagesProcessed={stats?.totalMessages || 0}
          messagesTrend={trends?.messagesTrend}
          sources={{
            approved: sourceStats?.approved || 0,
            pending: sourceStats?.pending || 0,
            unauthorized: sourceStats?.unauthorized || 0,
          }}
          domains={{
            total: stats?.totalDomains || 0,
            atEnforcement: stats?.domainsAtEnforcement || 0,
          }}
          loading={loading}
        />
      </div>

      {/* Needs Attention Card - Full width for visibility */}
      <NeedsAttentionCard maxItems={3} />
    </div>
  );
}
